<script setup lang="ts">
import { FocusTrap } from 'focus-trap-vue'
import {
  useSkyBreakpoint,
  SkyButton,
  SkyLink,
  SkyIcon,
  SkyImage,
} from '@groveco/skylight'
import type { MenuItem } from '@groveco/http-services'

// Constants
import {
  FEEL_GOOD_WELLNESS_STANDARD_LOGO_IMAGE,
  GROVE_WELLNESS_LOGO_IMAGE,
  WELLNESS_ITEM_ID,
} from '~/constants/wellness'
import { ROUTES } from '~/constants/routes'

type SkyButtonRef = InstanceType<typeof SkyButton> | null
interface ExpandedMenuItem extends MenuItem {
  parent: ExpandedMenuItem | null
}

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const {
  id: customerId,
  homeRoute,
  isMobileAppUser,
  isLoggedIn,
  isVip,
} = useCustomer()
const { menuItems } = useMenuItems()
const { isMediumUp } = useSkyBreakpoint()

const backButton = ref<SkyButtonRef>(null)
const expandedMenuItem = ref<ExpandedMenuItem | null>(null)
const menuItemRefs = ref<Record<string, SkyButtonRef>>({})
const touchStartX = ref(0)

const aboutMenuItem = computed<MenuItem>(() => {
  const children = [
    {
      id: 'about-story',
      text: 'Our Story',
      href: '/g/about/',
      children: [],
    },
    {
      id: 'about-standard',
      text: 'The Grove Standard',
      href: '/g/the-grove-standard/',
      children: [],
    },
    {
      id: 'about-subscribeAndSave',
      text: 'Subscribe and Save',
      href: '/learn-more-subscribe-and-save/',
      children: [],
    },
  ]

  if (isLoggedIn.value) {
    children.unshift({
      id: 'about-howItWorks',
      text: ROUTES.howGroveWorks.text,
      href: ROUTES.howGroveWorks.to,
      children: [],
    })
    children.push({
      id: 'about-contact',
      text: ROUTES.contactUs.text,
      href: ROUTES.contactUs.to,
      children: [],
    })
  }

  return {
    id: 'about',
    href: '',
    children,
    text: 'About',
  }
})

const accountMenuItem: MenuItem = {
  id: 'account',
  children: [
    {
      id: 'account-settings',
      text: 'Your Settings',
      href: '/settings/account/',
      children: [],
    },
    {
      id: 'account-refer',
      text: 'Refer Friends (Get $10)',
      href: '/refer/',
      children: [],
    },
    {
      id: 'account-cart',
      text: ROUTES.cartRoute.text,
      href: ROUTES.cartRoute.to,
      children: [],
    },
    {
      id: 'account-favorites',
      text: 'Favorites',
      href: '/saved-products/',
      children: [],
    },
    {
      id: 'account-subscriptions',
      text: 'Product Subscriptions',
      href: ROUTES.subscriptions.to,
      children: [],
    },
    {
      id: 'account-order-history',
      text: 'Order History',
      href: '/order-history/',
      children: [],
    },
    {
      id: 'account-review',
      text: 'Review Products',
      href: '/review/',
      children: [],
    },
  ],
  text: 'Your Account',
  href: ROUTES.logoutRoute.to,
}

const howItWorksMenuItem: MenuItem = {
  id: 'howItWorks',
  children: [
    {
      id: 'howItWorks-hiw',
      text: 'How It Works',
      href: '/g/how-grove-works/',
      children: [],
    },
    {
      id: 'howItWorks-subscribeAndSave',
      text: 'Subscribe and Save',
      href: '/learn-more-subscribe-and-save/',
      children: [],
    },
    {
      id: 'howItWorks-faq',
      text: 'FAQ',
      href: 'https://community.grove.co/hc/en-us/categories/200113215-FAQ/',
      children: [],
    },
    {
      id: 'howItWorks-contact',
      text: ROUTES.contactUs.text,
      href: ROUTES.contactUs.to,
      children: [],
    },
  ],
  href: '',
  text: 'How It Works',
}

const showVipDot = computed(() => {
  // Restore once useVipGiftPicker is ported
  // if (isVip.value) {
  //   const { hasNotification } = useVipGiftPicker()
  //   return hasNotification.value
  // }
  return false
})

const preloadGroveWellnessImages = () => {
  const topLogoImage = new Image()
  topLogoImage.src = GROVE_WELLNESS_LOGO_IMAGE

  const bottomLogoImage = new Image()
  bottomLogoImage.src = FEEL_GOOD_WELLNESS_STANDARD_LOGO_IMAGE
}

preloadGroveWellnessImages()

const expandMenuItem = (item: MenuItem) => {
  expandedMenuItem.value = {
    ...item,
    parent: expandedMenuItem.value,
  }

  // Shift focus to back button at the top
  nextTick(() => {
    backButton.value?.$el.focus()
  })
}

const showItemParent = () => {
  if (expandedMenuItem.value) {
    const { id } = expandedMenuItem.value
    expandedMenuItem.value = expandedMenuItem.value.parent
    // Shift focus back to menu item button
    nextTick(() => {
      menuItemRefs.value[id]?.$el?.focus()
    })
  }
}

const hideDrawer = () => {
  expandedMenuItem.value = null
  emit('hide')
}

const handleTouchEnd = (event: TouchEvent) => {
  const { changedTouches = [] } = event
  const touchEndX = changedTouches[0].screenX
  const travel = Math.abs(touchStartX.value - touchEndX)

  // Ignore short swipes
  if (travel < window.innerWidth / 2) {
    return
  }

  // Hide when swiping left or if the menu is not expanded
  if (touchStartX.value >= touchEndX || !expandedMenuItem.value) {
    hideDrawer()
    return
  }

  showItemParent()
}

const handleTouchStart = (event: TouchEvent) => {
  const { changedTouches = [] } = event
  touchStartX.value = changedTouches[0].screenX
}

const isGroveWellness = (menuItem: MenuItem) => menuItem.id === WELLNESS_ITEM_ID
</script>

<template>
  <div
    class="NavDrawer"
    data-test-id="nav-drawer"
    @click="hideDrawer"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <FocusTrap
      active
      fallback-focus=".NavDrawer_Container"
      @deactivate="hideDrawer"
    >
      <div class="NavDrawer_Container" tabindex="0">
        <nav v-if="!expandedMenuItem">
          <div class="NavDrawer_Header">
            <template v-if="customerId">
              <SkyLink
                v-if="isLoggedIn"
                class="NavDrawer_ReferLink"
                to="/refer"
              >
                Refer Friends, Get $10
              </SkyLink>
              <SkyLink
                v-else
                :to="ROUTES.loginRoute.to"
                class="NavDrawer_MenuItem"
                data-test-id="login-link"
              >
                Hello friend! Log in
              </SkyLink>
            </template>
          </div>
          <ul class="NavDrawer_List">
            <li v-if="isLoggedIn" class="NavDrawer_ListItem">
              <SkyLink
                :to="ROUTES.vipHub.to"
                :class="{
                  'NavDrawer_MenuItem--dot': showVipDot,
                }"
                class="NavDrawer_MenuItem NavDrawer_MenuItem--primary"
                data-test-id="viphub-link"
              >
                {{ !isVip ? 'Join VIP' : 'VIP Hub' }}
              </SkyLink>
            </li>
            <li v-if="isLoggedIn" class="NavDrawer_ListItem">
              <SkyLink
                v-if="homeRoute"
                :to="homeRoute"
                class="NavDrawer_MenuItem NavDrawer_MenuItem--primary"
              >
                Your Grove
              </SkyLink>
            </li>
            <li
              v-for="menuItem in menuItems"
              :key="menuItem.id"
              class="NavDrawer_ListItem"
            >
              <SkyButton
                v-if="menuItem.children.length"
                :ref="(el) => (menuItemRefs[menuItem.id] = el as SkyButtonRef)"
                class="NavDrawer_MenuItem NavDrawer_MenuItem--primary"
                data-test-id="item-expand"
                right-icon="chevron-right"
                text
                @click.stop="expandMenuItem(menuItem)"
              >
                {{ menuItem.text }}
              </SkyButton>
              <SkyLink
                v-else
                :to="menuItem.href"
                class="NavDrawer_MenuItem NavDrawer_MenuItem--primary"
                data-test-id="item-link"
              >
                {{ menuItem.text }}
              </SkyLink>
            </li>
            <li class="NavDrawer_ListItem">
              <SkyLink
                class="NavDrawer_MenuItem NavDrawer_MenuItem--primary"
                to="/catalog/?category=grove-collaborative"
              >
                Shop Grove Co.
              </SkyLink>
            </li>
            <li
              aria-hidden="true"
              class="NavDrawer_ListItem"
              role="presentation"
            >
              <hr class="NavDrawer_Divider" />
            </li>
            <li v-if="customerId" class="NavDrawer_ListItem">
              <SkyButton
                v-if="isLoggedIn"
                :ref="(el) => (menuItemRefs[accountMenuItem.id] = el as SkyButtonRef)"
                class="NavDrawer_MenuItem"
                data-test-id="account-expand"
                right-icon="chevron-right"
                text
                @click.stop="expandMenuItem(accountMenuItem)"
              >
                {{ accountMenuItem.text }}
              </SkyButton>
              <SkyLink
                v-else
                :to="ROUTES.cartRoute.to"
                class="NavDrawer_MenuItem"
              >
                {{ ROUTES.cartRoute.text }}
              </SkyLink>
            </li>
            <li class="NavDrawer_ListItem">
              <SkyButton
                :ref="(el) => (menuItemRefs[aboutMenuItem.id] = el as SkyButtonRef)"
                class="NavDrawer_MenuItem"
                data-test-id="about-expand"
                right-icon="chevron-right"
                text
                @click.stop="expandMenuItem(aboutMenuItem)"
              >
                {{ aboutMenuItem.text }}
              </SkyButton>
            </li>
            <li v-if="customerId && !isLoggedIn" class="NavDrawer_ListItem">
              <SkyButton
                :ref="(el) => (menuItemRefs[howItWorksMenuItem.id] = el as SkyButtonRef)"
                class="NavDrawer_MenuItem"
                data-test-id="hiw-expand"
                right-icon="chevron-right"
                text
                @click.stop="expandMenuItem(howItWorksMenuItem)"
              >
                {{ howItWorksMenuItem.text }}
              </SkyButton>
            </li>
            <li
              v-if="!isMediumUp && !isMobileAppUser"
              class="NavDrawer_ListItem"
            >
              <SkyLink
                class="NavDrawer_MenuItem"
                data-test-id="mobile-link"
                rel="noreferrer noopener"
                target="_blank"
                to="https://grove.onelink.me/rUOP/daabd0e9"
              >
                Download the Grove App
              </SkyLink>
            </li>
            <li class="NavDrawer_ListItem">
              <TheHeaderLogo />
            </li>
          </ul>
        </nav>
        <nav
          v-if="expandedMenuItem"
          :aria-labelledby="`expandedMenuItem-${expandedMenuItem.id}`"
        >
          <div class="NavDrawer_Header">
            <SkyButton
              ref="backButton"
              class="NavDrawer_MenuItem NavDrawer_MenuItem--back"
              data-test-id="back"
              left-icon="chevron-left"
              text
              @click.stop="showItemParent"
            >
              Back
            </SkyButton>
          </div>
          <SkyLink
            v-if="isGroveWellness(expandedMenuItem)"
            :to="ROUTES.wellnessRoute.to"
            unstyled
            data-test-id="expanded-heading-grove-wellness"
          >
            <SkyImage
              alt="Grove Wellness Logo"
              loading="eager"
              class="NavDrawer_GroveWellnessLogo"
              :src="GROVE_WELLNESS_LOGO_IMAGE"
              width="140"
            />
          </SkyLink>
          <h2
            v-else
            :id="`expandedMenuItem-${expandedMenuItem.id}`"
            class="NavDrawer_Heading"
            data-test-id="expanded-heading"
          >
            {{ expandedMenuItem.text }}
          </h2>
          <ul class="NavDrawer_List">
            <li
              v-for="child in expandedMenuItem.children"
              :key="child.id"
              class="NavDrawer_ListItem"
            >
              <SkyButton
                v-if="child.children && child.children.length"
                :ref="(el) => (menuItemRefs[child.id] = el as SkyButtonRef)"
                class="NavDrawer_MenuItem"
                data-test-id="expanded-expand"
                right-icon="chevron-right"
                text
                @click.stop="expandMenuItem(child)"
              >
                {{ child.text }}
              </SkyButton>
              <SkyLink
                v-else
                :to="child.href"
                class="NavDrawer_MenuItem"
                data-test-id="expanded-link"
              >
                {{ child.text }}
              </SkyLink>
            </li>
            <li v-if="expandedMenuItem.href" class="NavDrawer_ListItem">
              <hr aria-hidden="true" class="NavDrawer_Divider" />
              <SkyLink
                :to="expandedMenuItem.href"
                class="NavDrawer_MenuItem"
                data-test-id="expanded-link-self"
              >
                <template
                  v-if="expandedMenuItem.href === ROUTES.logoutRoute.to"
                >
                  {{ ROUTES.logoutRoute.text }}
                </template>
                <template v-else>Shop All {{ expandedMenuItem.text }}</template>
              </SkyLink>
            </li>
            <li v-if="isGroveWellness(expandedMenuItem)">
              <SkyLink
                :to="ROUTES.wellnessRoute.to"
                class="NavDrawer_WellnessPromise NavDrawer_MenuItem NavDrawer_MenuItem--primary"
                unstyled
              >
                <SkyImage
                  alt="Feel Good Wellness Standard Logo"
                  class="NavDrawer_FeelGoodLogo"
                  loading="eager"
                  :src="FEEL_GOOD_WELLNESS_STANDARD_LOGO_IMAGE"
                  height="34"
                  data-test-id="feel-good-logo"
                />
                <slot name="icon">
                  <SkyIcon name="chevron-right" size="18px" />
                </slot>
              </SkyLink>
            </li>
          </ul>
        </nav>
      </div>
    </FocusTrap>
  </div>
</template>

<style lang="scss">
.NavDrawer {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: var(--z-index-popout);

  &_Container {
    height: 100%;
    width: 80vw;
    max-width: calc(var(--spacing-24x) * 3);
    overflow-y: auto;
    background: var(--surface-color-dark);
    box-shadow: 0 0 var(--spacing-3x) var(--spacing-halfx)
      var(--surface-color-darker);
    padding: 0 var(--spacing-6x) 0 var(--spacing-8x);

    .SkyLink,
    .SkyButton {
      color: var(--text-color-action-primary-ondark);
      border: none;

      &:hover,
      &:focus {
        color: var(--text-color-action-hover-ondark);
      }

      &:focus-visible {
        outline-color: var(--text-color-action-secondary-ondark);
      }
    }
  }

  &_GroveWellnessLogo {
    .SkyImage_Image {
      object-fit: unset;
    }
  }

  &_FeelGoodLogo {
    object-fit: unset;
  }

  &_Header {
    min-height: var(--spacing-24x);
    display: flex;
  }

  &_Heading {
    @include type-heading('2');
    color: var(--text-color-action-primary-ondark);
    margin: 0;
  }

  &_Divider {
    max-width: 30%;
    margin: var(--spacing-4x) 0;
    border-color: var(--text-color-secondary);
  }

  &_List {
    margin: 0;
    padding: 0;
    list-style: none;

    &Item {
      padding: 0;
    }
  }

  &_MenuItem {
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: var(--spacing-4x) 0;
    &.SkyButton,
    &.SkyLink {
      line-height: var(--line-height-tight);
    }

    &--back {
      // Compensate for chevron-left icon
      margin-left: calc(var(--spacing-2x) * -1);
      justify-content: flex-start;
    }

    &--primary {
      margin: var(--spacing-1x) 0;

      &.SkyButton,
      &.SkyLink {
        font-size: var(--font-size-400);
        line-height: var(--line-height-default);
      }
    }

    &--dot {
      justify-content: start;
      align-items: unset;
      gap: var(--spacing-1x);

      &:after {
        content: 'You have a new gift to choose';
        background: var(--color-teal);
        border-radius: var(--border-radius-pill);
        box-shadow: 0 0 0 1px var(--color-frost);
        color: var(--color-teal);
        display: block;
        height: var(--spacing-2x);
        overflow: hidden;
        width: var(--spacing-2x);
      }
    }
  }

  &_WellnessPromise {
    height: 78px;
  }

  &_ReferLink {
    background: var(--surface-color-linen)
      url('https://images.grove.co/upload/v1635542950/Product%20Design/Navigation/leaf-texture-background-mobile.jpg')
      no-repeat;
    background-size: contain;
    text-align: center;
    display: flex;
    align-items: center;
    padding: 0 var(--spacing-8x);
    margin-bottom: var(--spacing-4x);

    &.SkyLink {
      font-family: var(--font-family-display);
      font-size: var(--font-size-300);
      color: var(--text-color-action-dark);

      &:hover,
      &:focus {
        color: var(--text-color-action-dark-hover);
      }
    }
  }
}
</style>
